// JSON-LD schema generators for SEO
import { COMPANY, SERVICE_AREAS } from "../mock";

const SITE_URL = (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "");

export const SITE = {
  url: SITE_URL,
  name: COMPANY.name,
  phone: COMPANY.phone,
  phoneRaw: "+1-678-525-0474",
  email: COMPANY.email,
  street: "2318 Newgate Drive",
  city: "Decatur",
  region: "GA",
  postal: "30035",
  country: "US",
  // Geographic center for the 25-mile service radius (2318 Newgate Drive, Decatur)
  lat: 33.7215,
  lng: -84.2257,
  radiusMeters: 40234, // 25 miles
};

// Social / directory profile slots for schema.org `sameAs`. Fill in as profiles go live;
// empty strings are skipped automatically.
export const SOCIAL_PROFILES = {
  googleBusinessProfile: "https://www.google.com/maps/place/?q=place_id:ChIJPZdyuFWr9YgR0mmxqdu-5h0",
  facebook: "https://www.facebook.com/p/Decatur-Solution-Works-61582999265006/",
  bingPlaces: "https://www.bing.com/maps/search?style=r&q=Decatur+Solution+Works+LLC&cp=33.726799%7E-84.221169&lvl=17",
  appleMaps: "https://maps.apple.com/place?place-id=IA5072E977C58564F&address=2318+Newgate+Dr%2C+Decatur%2C+GA++30035%2C+United+States&coordinate=33.726858%2C-84.221175&name=Decatur+Solution+Works&_provider=9902",
  threads: "https://www.threads.com/@decatursolutionworks",
  instagram: "https://www.instagram.com/decatursolutionworks/",
};

const WARRANTY_1Y = {
  "@type": "WarrantyPromise",
  "durationOfWarranty": { "@type": "QuantitativeValue", "value": 1, "unitCode": "ANN" },
  "warrantyScope": "https://schema.org/Labor-BringIn",
};

const WARRANTY_90D = {
  "@type": "WarrantyPromise",
  "durationOfWarranty": { "@type": "QuantitativeValue", "value": 90, "unitCode": "DAY" },
  "warrantyScope": "https://schema.org/Labor-BringIn",
};

// Exact combined LocalBusiness + BlogPosting @graph supplied by the client for every blog/article page.
export const blogGraphSchema = (post, headline, description) => {
  const pageUrl = `${SITE_URL}/blog/${post.slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": `${SITE_URL}/#localbusiness`,
        "name": "Decatur Solution Works LLC",
        "image": `${SITE_URL}/logo.png`,
        "url": SITE_URL,
        "telephone": "+16785250474",
        "priceRange": "$$",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "2318 Newgate Drive",
          "addressLocality": "Decatur",
          "addressRegion": "GA",
          "postalCode": "30035",
          "addressCountry": "US",
        },
        "geo": { "@type": "GeoCoordinates", "latitude": 33.7215, "longitude": -84.2257 },
        "openingHoursSpecification": [
          { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "08:00", "closes": "18:00" },
          { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Saturday", "Sunday"], "opens": "10:00", "closes": "18:00" },
        ],
        "areaServed": { "@type": "AdministrativeArea", "name": "DeKalb County" },
      },
      {
        "@type": "BlogPosting",
        "@id": `${pageUrl}/#article`,
        "isPartOf": { "@type": "WebPage", "@id": pageUrl },
        "headline": headline,
        "description": description,
        "inLanguage": "en-US",
        "mainEntityOfPage": pageUrl,
        "publisher": { "@id": `${SITE_URL}/#localbusiness` },
        "author": { "@type": "Organization", "name": "Decatur Solution Works LLC" },
        "spatialCoverage": {
          "@type": "Place",
          "name": "Decatur, DeKalb County, Georgia",
          "address": { "@type": "PostalAddress", "addressLocality": "Decatur", "addressRegion": "GA", "postalCode": "30035", "addressCountry": "US" },
          "geo": { "@type": "GeoCircle", "geoMidpoint": { "@type": "GeoCoordinates", "latitude": 33.7215, "longitude": -84.2257 }, "geoRadius": 40234 },
        },
        "keywords": "appliance repair Decatur GA, DeKalb County appliance repair, Tucker, Stone Mountain, Lithonia, Ellenwood, Stockbridge",
        ...(post.image ? { "image": post.image.startsWith("http") ? post.image : `${SITE_URL}${post.image}` } : {}),
        ...(post.published_at ? { "datePublished": post.published_at } : {}),
      },
    ],
  };
};

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
  "@id": `${SITE.url}/#business`,
  "name": "Decatur Solution Works LLC",
  "alternateName": ["Decatur Solution Works", "Decatur Solution Works Appliance Repair"],
  "image": `${SITE.url}/site-images/k8c76gt0_decatur-solution-works-home-base-office.webp`,
  "logo": `${SITE.url}/logo.png`,
  "url": SITE.url,
  "telephone": SITE.phoneRaw,
  "priceRange": "$$",
  "description":
    "Mobile appliance repair: same-day residential appliance repair and EPA 608 Type I certified commercial refrigeration service within a 25-mile radius of Decatur and DeKalb County, GA. Refrigerators, washers, dryers, ovens, stoves, dishwashers, walk-in coolers, reach-in freezers and ice machines. Samsung, LG, Whirlpool, GE, Sub-Zero, True and more. $89 diagnostic fee, credited toward your repair; $250 commercial diagnostic.",
  "foundingDate": "2007",
  "paymentAccepted": "Cash, Credit Card, Debit Card",
  "currenciesAccepted": "USD",
  "knowsAbout": [
    "Mobile appliance repair",
    "Refrigerator repair", "Washer repair", "Dryer repair", "Oven and stove repair", "Dishwasher repair",
    "Commercial refrigeration repair", "Walk-in cooler repair", "Reach-in freezer repair", "Ice machine repair",
    "EPA Section 608 refrigerant handling",
  ],
  "hasCredential": [
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "certification",
      "name": "EPA Section 608 Type I Technician Certification",
      "recognizedBy": { "@type": "GovernmentOrganization", "name": "U.S. Environmental Protection Agency" },
    },
  ],
  "address": {
    "@type": "PostalAddress",
    "streetAddress": SITE.street,
    "addressLocality": SITE.city,
    "addressRegion": SITE.region,
    "postalCode": SITE.postal,
    "addressCountry": SITE.country,
  },
  "geo": { "@type": "GeoCoordinates", "latitude": SITE.lat, "longitude": SITE.lng },
  "areaServed": SERVICE_AREAS.map((city) => ({
    "@type": "City",
    "name": city,
    "containedInPlace": { "@type": "State", "name": "Georgia" },
  })),
  "serviceArea": {
    "@type": "GeoCircle",
    "geoMidpoint": { "@type": "GeoCoordinates", "latitude": SITE.lat, "longitude": SITE.lng },
    "geoRadius": SITE.radiusMeters,
  },
  "openingHoursSpecification": [
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "08:00", "closes": "18:00" },
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Saturday", "Sunday"], "opens": "10:00", "closes": "18:00" },
  ],
  "sameAs": Object.values(SOCIAL_PROFILES).filter(Boolean),
  // aggregateRating intentionally omitted until live Google reviews are pulled (GCP billing) —
  // ratings in schema must be sourced from reviews visible on the page.
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Appliance Repair & Commercial Refrigeration Services",
    "itemListElement": [
      {
        "@type": "OfferCatalog",
        "name": "Residential Appliance Repair",
        "itemListElement": [
          "Refrigerator Repair", "Washer Repair", "Dryer Repair", "Oven & Stove Repair", "Gas Stove Repair",
          "Dishwasher Repair", "Ice Maker Repair", "Refrigerator Water Line Installation", "Appliance Installation",
        ].map((s) => ({
          "@type": "Offer",
          "priceCurrency": "USD",
          "price": 89,
          "description": "$89 diagnostic fee, credited toward your repair, EPA 608 Certified technician, 1-year parts & labor warranty (one year on OEM parts)",
          "warranty": WARRANTY_1Y,
          "itemOffered": { "@type": "Service", "name": s, "serviceType": s },
        })),
      },
      {
        "@type": "OfferCatalog",
        "name": "Commercial Refrigeration & Restaurant Equipment",
        "itemListElement": [
          "Walk-In Cooler Repair", "Walk-In Freezer Repair", "Reach-In Refrigerator & Freezer Repair",
          "Ice Machine Repair", "Commercial Refrigeration Rack Repair", "Commercial Appliance Repair",
          "Preventive Maintenance Plans",
        ].map((s) => ({
          "@type": "Offer",
          "priceCurrency": "USD",
          "price": 250,
          "description": "$250 flat-rate commercial diagnostic by EPA 608 Type I certified technicians, 90-day parts & labor warranty on commercial repairs",
          "warranty": WARRANTY_90D,
          "itemOffered": { "@type": "Service", "name": s, "serviceType": s },
        })),
      },
    ],
  },
});


export const serviceSchema = (page) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": page.title,
  "description": page.metaDescription,
  "serviceType": page.serviceType,
  "provider": { "@id": `${SITE.url}/#business` },
  "areaServed": SERVICE_AREAS.map((city) => ({ "@type": "City", "name": city })),
  "audience": { "@type": "Audience", "audienceType": "Homeowners and Businesses" },
  "offers": {
    "@type": "Offer",
    "priceCurrency": "USD",
    "price": page.startingAt || 89,
    "availability": "https://schema.org/InStock",
    "validFrom": new Date().toISOString().split("T")[0],
  },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": item.name,
    "item": `${SITE.url}${item.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
});
